import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/** Matches account-creation DOB field display (`MM / DD / YYYY`). */
const DISPLAY_FORMAT = 'MM / DD / YYYY';
const DATA_FORMAT = 'YYYY-MM-DD';

/**
 * Doubles as the DOB input mask (progressively formats typed digits) and the
 * API-value formatter (ISO → display). Distinguished by the `-` separator:
 * API values (`YYYY-MM-DD` / full ISO) contain it, typed input never does.
 */
export function formatBirthdayForDisplay(birthday?: string | null): string {
  if (!birthday?.trim()) return '';
  const trimmed = birthday.trim();

  // API ISO value → reformat via Day.js.
  if (trimmed.includes('-')) {
    const parsed = dayjs.utc(trimmed);
    return parsed.isValid() ? parsed.format(DISPLAY_FORMAT) : '';
  }

  // Typed input: mask the digits as MM / DD / YYYY. The final branch covers
  // 5–8 digits (a complete 8-digit DOB included) — previously 8 digits fell
  // through to a Day.js parse of the raw digit string, which is invalid and
  // wiped the field the moment the user finished typing.
  const digits = trimmed.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
  return `${digits.slice(0, 2)} / ${digits.slice(2, 4)} / ${digits.slice(4)}`;
}

export function formatBirthdayForData(birthday?: string | null): string {
  if (!birthday?.trim()) return '';
  const trimmed = birthday.trim();

  // Distinguished by `-` like formatBirthdayForDisplay: API ISO values
  // (`YYYY-MM-DD` / full ISO) contain it; typed/display values (`MM / DD /
  // YYYY`) never do. The display string must be parsed with an explicit format
  // — a format-less `dayjs()` of a slashed string falls through to the native
  // Date parser, which is Invalid Date on Hermes/JSC and silently wipes the
  // DOB. `customParseFormat` (strict) makes the parse deterministic.
  const parsed = trimmed.includes('-')
    ? dayjs.utc(trimmed)
    : dayjs.utc(trimmed, DISPLAY_FORMAT, true);
  return parsed.isValid() ? parsed.format(DATA_FORMAT) : '';
}
